import { buildQuery, getAPIHeaders, QueryParams } from '../../../utils/helpers';
import AppConfig from '../../../lib/AppConfig';
import { cleanApiError, handleResponse } from '../../../utils/handleResponse';
import { ListSavingsResponse } from '../types/response/ListSavings';
import { ListSavingsRequest, ListSavingsRequestLite } from '../types/request/ListSavings';
import { SavingProduct } from '../types/response/ListSavingProducts';
import { CreateSavingRequest } from '../types/request/CreateSaving';
import { CreateSavingResponse } from '../types/response/CreateSaving';
import { ClientAccounts } from '../types/response/ListClientSavings';
import {
	TActivateSaving,
	TApproveSaving,
	TDepositToSaving,
	TFundTransferRequest,
	TWithdrawFromSaving,
} from '../types/request/Saving';
import { TSavingTransactionTemplate } from '../types/response/SavingTemplate';
import { TSavingAccount } from '../types/response/Saving';
import { TSavingTransferTemplate } from '../types/response/TransferTemplate';
import { ISavingTransferTemplateRequest } from '../types/request/TransferTemplate';
import { AccountTransferDetails } from '../types/response/AccountTransfer';
import { SavingNote } from '../types/response/Note';
import { SavingCharge } from '../types/response/Charge';
import { SavingsResponseLite } from '../types/response/SavingResponseLite';
import { SavingsSummary } from '../types/response/Summary';
import { SavingProductTemplate } from '../types/response/ProductTemplate';

export namespace SavingsRepository {
	// createSaving
	export async function createSaving(request: CreateSavingRequest): Promise<CreateSavingResponse> {
		// Implementation can change
		try {
			const endPoint = `/fineract-provider/api/v1/savingsaccounts`;
			const url = `${AppConfig.api_url}${endPoint}`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'POST',
				body: JSON.stringify(request),
				headers: headers,
			});

			const data = await handleResponse(response);

			return data;
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// getSaving
	export async function getSaving(savingId: number): Promise<TSavingAccount> {
		try {
			const endPoint = `/fineract-provider/api/v1/savingsaccounts/${savingId}?associations=all`;
			const url = `${AppConfig.api_url}${endPoint}`;

			const response = await fetch(url, {
				headers: await getAPIHeaders(),
			});

			const data = await handleResponse(response);

			return data;
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// listSavings
	export async function listSavings(request: ListSavingsRequest): Promise<ListSavingsResponse> {
		// Implementation can change
		try {
			const pagination = `offset=${request.offset ?? 0}&limit=${request.limit ?? 100}`;
			const endPoint = `/fineract-provider/api/v1/savingsaccounts?${request.externalId ? `externalId=${request.externalId}` : ''}${pagination}`;
			const url = `${AppConfig.api_url}${endPoint}`;

			const response = await fetch(url, {
				headers: await getAPIHeaders(),
			});

			const data = await handleResponse(response);

			return data;
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// listSavingsLite retrieves small deetails about loans
	export async function listSavingsLite(
		request: ListSavingsRequestLite,
	): Promise<SavingsResponseLite> {
		// Implementation can change
		try {
			const qs = buildQuery(request as QueryParams);
			const url = `${AppConfig.api_url}/gmapis/savings${qs ? `?${qs}` : ''}`;
			const response = await fetch(url.toString(), {
				headers: await getAPIHeaders(),
			});
			return handleResponse(response);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// getSavingsSummary retrieves savings summary
	export async function getSavingsSummary(params: Record<string, any>): Promise<SavingsSummary> {
		const qs = buildQuery(params);
		const url = `${AppConfig.api_url}/gmapis/savings/summary${qs ? `?${qs}` : ''}`;
		const res = await fetch(url, { headers: await getAPIHeaders() });
		return handleResponse(res);
	}

	// listSavingsProducts
	export async function listSavingsProducts(): Promise<SavingProduct[]> {
		// Implementation can change
		try {
			const endPoint = `/fineract-provider/api/v1/savingsproducts`;
			const url = `${AppConfig.api_url}${endPoint}`;

			const response = await fetch(url, {
				headers: await getAPIHeaders(),
			});

			return await handleResponse(response);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}
	// listClientAccounts
	export async function listClientAccounts(clientId: number | string): Promise<ClientAccounts> {
		// Implementation can change
		try {
			const endPoint = `/fineract-provider/api/v1/clients/${clientId}/accounts`;
			const url = `${AppConfig.api_url}${endPoint}`;

			const response = await fetch(url, {
				headers: await getAPIHeaders(),
			});

			return await handleResponse(response);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// activateSaving
	export async function activateSaving(
		savingId: number | string,
		req: TActivateSaving,
	): Promise<any> {
		// Implementation can change
		try {
			const endPoint = `/fineract-provider/api/v1/savingsaccounts/${savingId}?command=activate`;
			const url = `${AppConfig.api_url}${endPoint}`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'POST',
				headers,
				body: JSON.stringify(req),
			});

			return await handleResponse(response);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// approveSaving
	export async function approveSaving(savingId: number, req: TApproveSaving): Promise<any> {
		// Implementation can change
		try {
			const endPoint = `/fineract-provider/api/v1/savingsaccounts/${savingId}?command=approve`;
			const url = `${AppConfig.api_url}${endPoint}`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'POST',
				headers,
				body: JSON.stringify(req),
			});

			return await handleResponse(response);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// withdrawFromSaving
	export async function withdrawFromSaving(
		savingId: number,
		req: TWithdrawFromSaving,
	): Promise<any> {
		try {
			const endPoint = `/fineract-provider/api/v1/savingsaccounts/${savingId}/transactions?command=withdrawal`;
			const url = `${AppConfig.api_url}${endPoint}`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'POST',
				headers,
				body: JSON.stringify(req),
			});

			return await handleResponse(response);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// depositToSaving
	export async function depositToSaving(savingId: number, req: TDepositToSaving): Promise<any> {
		try {
			const endPoint = `/fineract-provider/api/v1/savingsaccounts/${savingId}/transactions?command=deposit`;
			const url = `${AppConfig.api_url}${endPoint}`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'POST',
				headers,
				body: JSON.stringify(req),
			});

			return await handleResponse(response);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// getSavingTransactionTemplate
	export async function getSavingTransactionTemplate(
		savingId: number,
	): Promise<TSavingTransactionTemplate> {
		try {
			const endPoint = `/fineract-provider/api/v1/savingsaccounts/${savingId}/transactions/template`;
			const url = `${AppConfig.api_url}${endPoint}`;

			const response = await fetch(url, {
				headers: await getAPIHeaders(),
			});

			return await handleResponse(response);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// getSavingTransactionTemplate
	export async function getSavingProductTransactionTemplate(
		productId: number,
	): Promise<SavingProductTemplate> {
		try {
			const endPoint = `/fineract-provider/api/v1/savingsproducts/${productId}?template=true`;
			const url = `${AppConfig.api_url}${endPoint}`;

			const response = await fetch(url, {
				headers: await getAPIHeaders(),
			});

			return await handleResponse(response);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// getAccountTransferTemplate
	export async function getAccountTransferTemplate(
		req: ISavingTransferTemplateRequest,
	): Promise<TSavingTransferTemplate> {
		try {
			let endPoint = `/fineract-provider/api/v1/accounttransfers/template?fromAccountId=${req.fromAccountId}&fromAccountType=${req.fromAccountType}`;
			if (req.toAccountId) {
				endPoint += `&toAccountId=${req.toAccountId}`;
			}
			if (req.toAccountType) {
				endPoint += `&toAccountType=${req.toAccountType}`;
			}
			if (req.toClientId) {
				endPoint += `&toClientId=${req.toClientId}`;
			}
			if (req.toOfficeId) {
				endPoint += `&toOfficeId=${req.toOfficeId}`;
			}

			const url = `${AppConfig.api_url}${endPoint}`;
			const response = await fetch(url, {
				headers: await getAPIHeaders(),
			});

			return await handleResponse(response);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// accountFundTransfer
	export async function accountFundTransfer(req: TFundTransferRequest): Promise<any> {
		try {
			const endPoint = `/fineract-provider/api/v1/accounttransfers`;
			const url = `${AppConfig.api_url}${endPoint}`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'POST',
				headers,
				body: JSON.stringify(req),
			});

			return await handleResponse(response);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// getAccountFundTransfer
	export async function getAccountFundTransfer(
		id: string | number,
	): Promise<AccountTransferDetails> {
		try {
			const endPoint = `/fineract-provider/api/v1/accounttransfers/${id}`;
			const url = `${AppConfig.api_url}${endPoint}`;

			const headers = await getAPIHeaders();

			const response = await fetch(url, {
				headers,
			});

			return await handleResponse(response);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	export async function printReceipt({ txId }: { txId: number }) {
		try {
			const endPoint = `/fineract-provider/api/v1/runreports/Savings%20Transaction%20Receipt?tenantIdentifier=default&locale=en&dateFormat=dd MMMM yyyy&output-type=PDF&R_transactionId=${txId}`;

			const url = `${AppConfig.api_url}${endPoint}`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				headers,
			});

			const blob = await handleResponse(response);

			const blobUrl = URL.createObjectURL(blob);

			return blobUrl;
		} catch (error) {
			throw cleanApiError(error);
		}
	}

	export async function unDo({
		accountsId,
		transactionId,
	}: {
		accountsId: number;
		transactionId: number;
	}) {
		try {
			const endPoint = `/fineract-provider/api/v1/savingsaccounts/${accountsId}/transactions/${transactionId}?command=undo`;

			const url = `${AppConfig.api_url}${endPoint}`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'POST',
				headers,
			});

			return await handleResponse(response);
		} catch (error) {
			throw cleanApiError(error);
		}
	}

	export async function hold({
		accountsId,
		transactionId,
	}: {
		accountsId: number;
		transactionId: number;
	}) {
		try {
			const endPoint = `/fineract-provider/api/v1/savingsaccounts/${accountsId}/transactions?command=holdAmount`;

			const url = `${AppConfig.api_url}${endPoint}`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'POST',
				headers,
			});

			return await handleResponse(response);
		} catch (error) {
			throw cleanApiError(error);
		}
	}

	export async function release({
		accountsId,
		transactionId,
	}: {
		accountsId: number;
		transactionId: number;
	}) {
		try {
			const endPoint = `/fineract-provider/api/v1/savingsaccounts/${accountsId}/transactions/${transactionId}?command=releaseAmount`;

			const url = `${AppConfig.api_url}${endPoint}`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'POST',
				headers,
			});

			return await handleResponse(response);
		} catch (error) {
			throw cleanApiError(error);
		}
	}

	export async function adjustTransaction({
		accountsId,
		transactionId,
		data,
	}: {
		accountsId: number;
		transactionId: number;
		data: any;
	}) {
		try {
			const endPoint = `/fineract-provider/api/v1/savingsaccounts/${accountsId}/transactions/${transactionId}?command=modify`;

			const url = `${AppConfig.api_url}${endPoint}`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'POST',
				body: JSON.stringify(data),
				headers,
			});

			const blob = await handleResponse(response);

			const blobUrl = URL.createObjectURL(blob);

			return blobUrl;
		} catch (error) {
			throw cleanApiError(error);
		}
	}

	export async function getSavingCharge(
		savingId: string | number,
		chargeId: string | number,
	): Promise<SavingCharge> {
		try {
			const endPoint = `/fineract-provider/api/v1/savingsaccounts/${savingId}/charges/${chargeId}`;

			const url = `${AppConfig.api_url}${endPoint}`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				headers,
			});

			const data = await handleResponse(response);

			return data;
		} catch (error) {
			throw cleanApiError(error);
		}
	}

	export async function getSavingNotes(savingId: string | number): Promise<SavingNote[]> {
		try {
			const endPoint = `/fineract-provider/api/v1/savings/${savingId}/notes`;

			const url = `${AppConfig.api_url}${endPoint}`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				headers,
			});

			const data = await handleResponse(response);

			return data;
		} catch (error) {
			throw cleanApiError(error);
		}
	}

	export async function updateSavingNote(
		savingId: string | number,
		noteId: string | number,
		note: SavingNote,
	): Promise<any> {
		try {
			const endPoint = `/fineract-provider/api/v1/savings/${savingId}/notes/${noteId}`;

			const url = `${AppConfig.api_url}${endPoint}`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'PUT',
				body: JSON.stringify(note),
				headers,
			});

			const data = await handleResponse(response);

			return data;
		} catch (error) {
			throw cleanApiError(error);
		}
	}

	export async function createSavingNote(
		savingId: string | number,
		note: SavingNote,
	): Promise<any> {
		try {
			const endPoint = `/fineract-provider/api/v1/savings/${savingId}/notes`;

			const url = `${AppConfig.api_url}${endPoint}`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'POST',
				body: JSON.stringify(note),
				headers,
			});

			const data = await handleResponse(response);

			return data;
		} catch (error) {
			throw cleanApiError(error);
		}
	}

	export async function deleteNote(
		savingId: string | number,
		noteId: string | number,
	): Promise<any> {
		try {
			const endPoint = `/fineract-provider/api/v1/savings/${savingId}/notes/${noteId}`;

			const url = `${AppConfig.api_url}${endPoint}`;

			const headers = await getAPIHeaders();

			const response = await fetch(url, {
				headers,
				method: 'DELETE',
			});

			const data = await handleResponse(response);

			return data;
		} catch (error) {
			throw cleanApiError(error);
		}
	}
}
